import { Directive, OnDestroy, Input, Output, EventEmitter } from '@angular/core';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { TwitterAvatarSize } from '../../../../Common/Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ListPickerService } from '../../../RhpContexts/services/list-picker.service';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { SortFieldEnum } from '../../../TargetingShared/enum/SortField.enum';
import { CirculationModel, CirculationType } from '../../../../Discover/Targeting/models/circulation.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { DistributionSummaryModel } from '../../model/distribution-summary-model';
import { DistributionPickerService } from '../../../RhpContexts/services/distribution-picker.service';
import { TargetingAddToTypeEnum } from '../../../../Discover/Targeting/enums/targeting-add-to-type.enum';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';

@Directive()
export abstract class ResultCardDirective implements OnDestroy {
    protected subscriptions: Subscription[] = [];

    entityInternal: TargetingResultModel;

    @Input() id: string;
    @Input() set entity(value: TargetingResultModel) {
        this.entityInternal = value;
        this.updateListMembershipInfo();
        this.setDisplayedReachValue();
    }

    @Input() disableSelection: boolean = false;
    @Input() initiallyFocusable: boolean = true;
    @Input() addToType: TargetingAddToTypeEnum = TargetingAddToTypeEnum.LIST;

    @Input() selectedResults: TargetingResultModel[];
    @Output() selectionChanged: EventEmitter<any> = new EventEmitter();

    expanded: boolean = false;

    workingLists: TargetingListList[];
    workingDists: DistributionSummaryModel[];
    membershipRequest:  Subscription;

    listMemberships: TargetingList[] = [];
    listMembershipTooltip: string;

    distMemberships: DistributionSummaryModel[] = [];
    distMembershipTooltip: string;

    readonly buttonTypes = ButtonTypesEnum;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly targetingAddToTypes = TargetingAddToTypeEnum;
    readonly attributeTypes = attributeTypesEnum;

    sortReachUnit: SortFieldEnum;
    sortCirculationType: CirculationType;
    reachUnit: string;
    reachValue: number;
    reachIcon: string;
    isReachValueFromSimilarWeb: boolean = false;

    constructor(
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        public listPickerService: ListPickerService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public distributionPickerService: DistributionPickerService,
    ) {
        this.subscriptions.push(
            this.listPickerService.getWorkingLists().pipe(filter(x => !!x)).subscribe(lists => {
                this.workingLists = lists;
                this.updateListMembershipInfo();
            }),
            // After an add operation, the list picker will emit this event
            // to notify all card instances that their blue checkmark needs to be updated
            // in case their specific entity's memberships have changed
            this.listPickerService.getListMembershipsChanged().subscribe(done => this.updateListMembershipInfo()),
            this.distributionPickerService.getWorkingDistributions().pipe(filter(x => !!x)).subscribe(dists => {
                this.workingDists = dists;
                this.updateDistMembershipInfo();
            }),
            this.targetingListService.getListMembershipsChanged().pipe(filter(x => !!x)).subscribe(id => {
                if (this.entityInternal && (this.entityInternal.id === id)){
                    this.fetchListMembershipInfo();
                }
            }),
            this.targetingSearchService.getSortField().subscribe(x => {
                if (x) {
                    this.sortReachUnit = x.enumValue;
                    this.setDisplayedReachValue();
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    updateListMembershipInfo() {
        if (!this.entityInternal || !this.entityInternal.listMemberships) {
            return;
        }
        this.updateDistMembershipInfo();

        this.listMemberships = this.entityInternal.listMemberships.filter(x => this.workingLists.find(y => y.id === x.id));

        if (this.listMemberships.length === 0) {
            this.listMembershipTooltip = null;
            return;
        }

        const listString = this.listMemberships.map(x => x.name).join(', ');

        let tooltipHeader;
        if (this.listMemberships.length === 1) {
            tooltipHeader = this.localizationService.transform('Discover.targeting.result.inonelist');
        } else if (this.listMemberships.length > 1) {
            tooltipHeader = this.localizationService.transform(
                'Discover.targeting.result.inseverallists',
                {
                    count: this.listMemberships.length,
                }
            ) ;
        }

        this.listMembershipTooltip =
        '<div style="font-weight:700;">' +
            tooltipHeader +
        '</div>' +
        listString;
    }

    updateDistMembershipInfo() {
        if (!this.entityInternal || !this.entityInternal.listMemberships) {
            return;
        }

        this.distMemberships = this.workingDists.filter(x => this.entityInternal.listMemberships.find(y => y.id === x.individualRecipientsListId));

        if (this.distMemberships.length === 0) {
            this.distMembershipTooltip = null;
            return;
        }

        const distString = this.distMemberships.map(x => x.name).join(', ');

        let tooltipHeader;
        if (this.distMemberships.length === 1) {
            tooltipHeader = this.localizationService.transform('Discover.targeting.result.inoneDist');
        } else if (this.distMemberships.length > 1) {
            tooltipHeader = this.localizationService.transform(
                'Discover.targeting.result.inseveralDists',
                {
                    count: this.distMemberships.length,
                }
            ) ;
        }

        this.distMembershipTooltip =
        '<div style="font-weight:700;">' +
            tooltipHeader +
        '</div>' +
        distString;
    }

    onCardClicked(entity: TargetingResultModel) {
        this.targetingRhpContextsService.setEntityByResult(entity);
    }

    checkboxClick() {
        this.selectionChanged.emit(this.selectedResults);
    }

    fetchListMembershipInfo(): void {
        if (this.membershipRequest) {
            this.membershipRequest.unsubscribe();
            this.membershipRequest = null;
        }

        const entityListKey = this.entityInternal?.entityType === EntityType.CONTACT ? 'contactUuid' : 'outletUuid';
        this.membershipRequest =  this.targetingListService.getListMemberships([{
            [entityListKey]: this.entityInternal?.id
        }]).subscribe(listMemberships => {
            this.entityInternal.listMemberships = listMemberships[0]?.targetingLists?.map(x => new TargetingList(x)) || [];
            this.updateListMembershipInfo();
        });
    }

    setDisplayedReachValue() {
        this.reachValue = null;
        if (this.entityInternal && this.sortReachUnit) {
            let circulations: CirculationModel[];

            if (this.entityInternal.entityType === EntityType.OUTLET) {
                circulations = this.entityInternal.circulations;
            } else {
                circulations = this.entityInternal.selectedEmployment?.outlet?.circulations;
            }

            if (circulations) {
                switch (this.sortReachUnit) {
                    case SortFieldEnum.CIRCULATION_CIRCULATION:
                        this.reachUnit = 'Discover.targeting.sortFields.circulation';
                        this.sortCirculationType = CirculationType.circulation;
                        this.reachValue = circulations.find(x => x.circulationType.enumValue === this.sortCirculationType)?.value;
                        this.reachIcon = 'fa-solid fa-arrows-spin';
                        break;
                    case SortFieldEnum.CIRCULATION_FOLLOWERS:
                        this.reachUnit = 'Discover.targeting.sortFields.followers';
                        this.sortCirculationType = CirculationType.followers;
                        this.reachValue = circulations.find(x => x.circulationType.enumValue === this.sortCirculationType)?.value;
                        this.reachIcon = 'fa-solid fa-user-group';
                        break;
                    case SortFieldEnum.CIRCULATION_LISTENERS:
                        this.reachUnit = 'Discover.targeting.sortFields.listeners';
                        this.sortCirculationType = CirculationType.listeners;
                        this.reachValue = circulations.find(x => x.circulationType.enumValue === this.sortCirculationType)?.value;
                        this.reachIcon = 'fa-solid fa-head-side-headphones';
                        break;
                    case SortFieldEnum.CIRCULATION_VIEWERS:
                        this.reachUnit = 'Discover.targeting.sortFields.viewers';
                        this.sortCirculationType = CirculationType.viewers;
                        this.reachValue = circulations.find(x => x.circulationType.enumValue === this.sortCirculationType)?.value;
                        this.reachIcon = 'fa-solid fa-eye';
                        break;
                    default:
                        this.reachUnit = 'Discover.targeting.sortFields.uniqueVisitors';
                        this.reachIcon = 'fa-solid fa-id-badge';
                        const circulation = circulations.find(x =>
                            x.circulationType.enumValue === CirculationType.manualValue || x.circulationType.enumValue === CirculationType.similarWebValue
                        );
                        if(circulation){
                            this.sortCirculationType = circulation.circulationType.enumValue as CirculationType;
                            this.reachValue = circulation.value;
                            this.isReachValueFromSimilarWeb = circulation.isSimilarWeb;
                        }
                        break;
                }
            }
        }
    }

    onKeydown(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (target?.classList?.contains('card') && (event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            this.onCardClicked(this.entityInternal);
        }
    }
}
